import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  initiateMediaUpload,
  invalidateUploadError,
  searchMedia,
  updateMediaUploadProgress,
  uploadMedia
} from "actions/media";

import _ from "lodash";
import cx from "classnames";
import shortid from 'shortid';
import { IKImage } from 'imagekitio-react';

import Dropzone from 'react-dropzone';
import S3Upload from "react-s3-uploader/s3upload";
import { toast } from "react-toastify";
import { FailureToast } from "components/Toast";
import { Button, Nav, Tab, Image, InputGroup, Form, Dropdown } from "react-bootstrap";

import { MediaUploadButton, MediaUploadIndicator } from "../index";
import InfiniteScroll from "components/InfiniteScroll";

import AuthService from 'services/auth.service';

import { ShortLoader } from "components/ApplicationLoader";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import styles from "./mediaPalette.module.scss";

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;
const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

const filterTypeOption = [
  {
    label: "Name",
    value: "name",
  },
  {
    label: "Date",
    value: "modifiedDate",
  },
];

export const MediaPaletteView = ({ storyboard, target, mediaTypes = ['image', 'video'], multiple, handleMediaLibrary, onSelect, mediaLibrary }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    active: 'image',
    selected: []
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({ sortType: "modifiedDate", sortOrder: "DESC" });
  const [loaderType, setLoaderType] = useState('');

  const uploads = useSelector(state => _.get(state, 'media.uploads')) || [];
  const mediaTarget = useSelector(state => _.get(state, 'media.mediaTarget')) || null;
  const mediaList = useSelector(state => _.get(state, 'media.items')) || [];
  const dataFetched = useSelector(state => _.get(state, 'media.fetched'));
  const loading = useSelector(state => _.get(state, 'media.loading'));
  const pagination = useSelector(state => _.get(state, 'media.pagination'));
  const uploadError = useSelector(state => _.get(state, 'media.uploadError'));
  const uploadErrorType = useSelector(state => _.get(state, 'media.errorType'));
  const uploadingFail = useSelector((state) => _.get(state.media.externalMedia, "uploadError", false));
  const hasMoreItems = _.get(pagination, 'currentPage') < _.get(pagination, 'totalPages') - 1;

  useEffect(() => {
    if(!loading) setLoaderType('');
  }, [loading]);

  useEffect(() => {
    if(uploadingFail || uploadError) dispatch(invalidateUploadError());
  }, [uploadError, uploadingFail, uploadErrorType]);

  useEffect(() => {
    if (!mediaLibrary) {
      handleSearchMedia(mediaTypes);
      setLoaderType('screen');
    }
  }, [mediaLibrary]);

  useEffect(() => {
    handleSearchMedia();
    setLoaderType('screen');
  }, [selectedFilters]);

  const handleTabChange = (active) => {
    if(!['video', 'image'].includes(active)) return;

    setState(state => {
      return {
        ...state,
        active
      }
    });
  }

  const openMediaLibrary = (e) => {
    e.stopPropagation()
    handleMediaLibrary()
  }

  const loadMoreMedia = (mediaType) => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    const request = {
      storyboard: storyboard.id,
      target: target,
      page: pagination.currentPage + 1,
      mediaTypes: mediaType,
      limit: 20,
      sort: selectedFilters.sortType,
      order: selectedFilters.sortOrder,
      query: searchQuery,
    };

    dispatch(searchMedia(request));
  }

  const handleClick = (media) => {
    var updated = [...state.selected];

    if (multiple) {
      const exists = updated.find(m => m.id === media.id) != null;
      if (exists) {
        updated = updated.filter(m => m.id !== media.id);
      } else {
        updated = [...updated, media];
      }
    } else {
      updated = [media];
    }
    
    setState(state => {
      return {
        ...state,
        selected: updated
      }
    })
  }

  const renderTabsIcon = (icon, title) => {
    return (
      <div className={styles.tabsButton}>
        <i className={`cleverstory-${icon}`} />
        <span className={styles.tabsTitle}>{title}</span>
      </div>
    )
  }

  const handleSearchMedia = (mediaTypes) => {
    const request = {
      storyboard: _.get(storyboard, 'id'),
      target: target,
      mediaTypes: mediaTypes,
      page: 0,
      limit: 20,
      sort: selectedFilters.sortType,
      order: selectedFilters.sortOrder,
      query: searchQuery,
    };

    dispatch(searchMedia(request));
  }

  const onFilterChange = (type, value) => {
    setSelectedFilters((state) => {
      return {
        ...state,
        [type]: value,
      };
    });
  };

  const activeDropdownLabel = _.find(filterTypeOption, (data)=>{
    return data.value == selectedFilters.sortType;
  });

  const activeMediaList = useMemo(() => {
    return mediaList.filter((media) => {
      const isVideo = media.type === 'video';
      return (state.active === 'video' && isVideo) || (state.active === 'image' && !isVideo);
    });
  }, [mediaList, state.active]);

  const isEmpty = dataFetched && uploads.length === 0 && activeMediaList.length === 0;

  return (
    <div className={styles.mediaPalette}>
      <Tab.Container
        id="left-tabs-example"
        defaultActiveKey="image"
        onSelect={handleTabChange}
      >
        <div className={cx(styles.palatteHeader, "handle")}>
          <Nav className={styles.navigationTabs}>
            <Nav.Item>
              <Nav.Link active={state.active === "image"} eventKey="image">
                {renderTabsIcon("background", "images")}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link active={state.active === "video"} eventKey="video">
                {renderTabsIcon("video", "videos")}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <MediaUploadButton
            className={styles.mediaFileUpload}
            storyboard={storyboard}
            target={target}
            mediaTypes={mediaTypes}
            onSelect={openMediaLibrary}
            handleActiveMedia={(active) => {
              setState((prev) => ({ ...prev, active }));
            }}
            name={"Upload"}
          />
        </div>
        <div className={styles.searchWrapper}>
          <div className={styles.search}>
            <InputGroup className={styles.searchBox}>
              <Form.Control
                className={styles.searchInput}
                placeholder="Search"
                aria-label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-describedby="Pexel search"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchMedia();
                    setLoaderType("search");
                  }
                }}
              />
              <InputGroup.Text
                className={styles.searchIcon}
                id="basic-addon1"
                onClick={() => {
                  handleSearchMedia();
                  setLoaderType("search");
                }}
              >
                {loaderType === "search" ? (
                  <i
                    className={cx(styles.loadIcon, "cleverstory-spinloader")}
                  />
                ) : (
                  <i className="cleverstory-search" />
                )}
              </InputGroup.Text>
            </InputGroup>
          </div>
          <div className={styles.actionWrapper}>
            <label className={styles.filterLabel}>Sort by:</label>
            <label className={styles.filterLabel}>
              {selectedFilters.sortOrder && (
                <img
                  onClick={() =>
                    onFilterChange(
                      "sortOrder",
                      selectedFilters.sortOrder === "ASC" ? "DESC" : "ASC"
                    )
                  }
                  className={cx(
                    styles.sortIcon,
                    selectedFilters.sortOrder === "ASC"
                      ? styles.ascending
                      : styles.descending
                  )}
                  alt="sortIcon"
                  src={"/images/sortIcon.svg"}
                />
              )}
            </label>
            <div className={styles.sortbyLabel}>
              <Dropdown className={styles.storboardTypeDropdown}>
                <Dropdown.Toggle id="dropdown-custom-components">
                  {activeDropdownLabel.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {filterTypeOption.map((option, index) => {
                    return (
                      <Dropdown.Item
                        active={option.value == selectedFilters.sortType}
                        onClick={() => onFilterChange("sortType", option.value)}
                        key={index}
                        eventKey={index}
                      >
                        {option.label}
                      </Dropdown.Item>
                    );
                  })}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </div>
        {loaderType === "screen" ? (
          <div className={styles.DragDropPaletteContainer}>
            <ShortLoader
              className={styles.loadingAnimation}
              title="Loading..."
            />
          </div>
        ) : (
          <MediaPaletteDragDrop
            mediaTarget={mediaTarget}
            mediaTypes={state.active}
            storyboardId={storyboard?.id || ""}
            target={target}
            setState={setState}
          >
            {() =>
              isEmpty ? (
                <EmptyMessage storyboard={storyboard} target={target} mediaTypes={mediaTypes} openMediaLibrary={openMediaLibrary} handleActiveMedia={(active) => {
                  setState((prev) => ({ ...prev, active }));
                }} />
              ) : (
                <div className={styles.tabContentView}>
                  <InfiniteScroll
                    className={cx(styles.scrollable)}
                    loading={loading}
                    hasMore={hasMoreItems}
                    loadMore={() => {
                      loadMoreMedia();
                    }}
                  >
                    <Tab.Content>
                      {state.active === "image" && (
                        <Tab.Pane eventKey="image">
                          {uploads.map((upload) => {
                            if (["video/mp4"].includes(upload.type)) return;

                            return (
                              <MediaUploadIndicator
                                key={upload.id}
                                media={upload}
                                instance={upload.instance}
                                onDimensionChange={(dimension) => {
                                  upload.instance.files[0].dimension = dimension;
                                }}
                                className={styles.mediaUploadProgress}
                              />
                            );
                          })}
                          {mediaList.map((media) => {
                            let active = state.selected.some(
                              (data) => data.id === media.id
                            );
                            if (media.type !== "image") {
                              return null;
                            }
                            return (
                              <Media
                                key={media.id}
                                media={media}
                                selected={active}
                                onClick={() => {
                                  handleClick(media);
                                }}
                              />
                            );
                          })}
                        </Tab.Pane>
                      )}
                      {state.active === "video" && (
                        <Tab.Pane eventKey="video">
                          {uploads.map((upload) => {
                            if (!["video/mp4"].includes(upload.type)) return;

                            return (
                              <MediaUploadIndicator
                                key={upload.id}
                                media={upload}
                                instance={upload.instance}
                                onDimensionChange={(dimension) => {
                                  upload.instance.files[0].dimension = dimension;
                                }}
                                className={styles.mediaUploadProgress}
                              />
                            );
                          })}
                          {mediaList.map((media) => {
                            let active = state.selected.some(
                              (data) => data.id === media.id
                            );
                            if (media.type !== "video") {
                              return null;
                            }
                            return (
                              <Video
                                key={media.id}
                                muted
                                media={media}
                                selected={active}
                                onClick={() => {
                                  handleClick(media);
                                }}
                              />
                            );
                          })}
                        </Tab.Pane>
                      )}
                    </Tab.Content>
                  </InfiniteScroll>
                </div>
              )
            }
          </MediaPaletteDragDrop>
        )}
      </Tab.Container>
      <div className={styles.actionContainer}>
        <Button
          variant="primary"
          disabled={_.size(state.selected) === 0}
          onClick={() => {
            onSelect(state.selected);
          }}
          className={styles.actionButton}
        >
          Add to editor
        </Button>
      </div>
    </div>
  );
}

export const Media = ({ media, selected, onClick }) => {

  return (
    <div className={cx(styles.media)} onClick={onClick}>
      <div className={cx(styles.icon, { [styles.selected]: selected })}>
        <IKImage path={media.url}
          transformation={[{
            "width": "600"
          }]}
          loading="lazy" />
      </div>
      <div className={styles.name}>
        <i className={cx("cleverstory-background")} />
        <TruncatedTextWithTooltip maxWidth={80} placement='top' content={media.name} />
      </div>
    </div>
  );
}

export const Video = ({ media, selected, onClick }) => {
  const videoRef = React.useRef(null);

  useEffect(() => {
    if (selected) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [selected]);

  return (
    <div className={cx(styles.media)} onClick={onClick}>
      <div className={cx(styles.icon, styles.videoIcon, { [styles.selected]: selected })}>
        <video name="media" ref={videoRef} className={styles.videoPlayer} autoPlay loop muted>
          <source src={`${IMAGE_CDN_ENDPOINT}/${media.url}`} />
        </video>
      </div>
      <div className={styles.name}>
        <i className={cx("cleverstory-video")} />
          <TruncatedTextWithTooltip maxWidth={80} placement='top' content={media.name} />
      </div>
    </div>
  )
}

export const EmptyMessage = ({storyboard, target, mediaTypes, openMediaLibrary, handleActiveMedia, disabled=false}) => {
  return (
    <div className={styles.emptyMediaContainer}>
      <div className={styles.emptyMessage}>
        <div className={styles.errorMessageContent}>
          <div className={styles.errorMessageTitle}>
            <h2 className={styles.heading}>Weather forecast</h2>
          </div>
          <div className={styles.errorMessageSubText}>
            <span>Clear skies and <br /> empty screens</span>
          </div>
        </div>
        {!disabled && 
          <Button variant="light" className={styles.emptyUploadButton}>
            <i className={cx('cleverstory-upload', styles.uploadIcon)} />Upload Media
            <MediaUploadButton
              className={styles.fileUpload}
              storyboard={storyboard} 
              target={target}
              mediaTypes={mediaTypes}
              onSelect={openMediaLibrary}
              handleActiveMedia={handleActiveMedia}
            />
          </Button>
        }
        <img className={cx(styles.emptyMediaImage)} src='' alt="emptyMedia_image" />
      </div>
    </div>
  )
}

export const MediaPaletteDragDrop = ({
  mediaTarget,
  mediaTypes,
  storyboardId,
  target,
  setState,
  children,
}) => {  

  const formats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml', 'video/mp4'];

  const dispatch = useDispatch();

  const removeSpecialCharactersFromName = (name)=>{
    return name.replace(/\u202F/g, '');
  }

  const handleUploadProgress = (progress, textState, file) => {
    dispatch(updateMediaUploadProgress(file.id, progress));
  }

  const handleFileCheck = (file,next)=>{
    const fileSizeExceeded = (file.size / 1048576) > 15

    if (file.type === 'video/mp4' && fileSizeExceeded) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`The video size should be less than 15 MB`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );
    } else {
      next(file);
    }
  }

  const handleUploadComplete = (info, file) => {
    if (info && formats.includes(file.type)) {
      var request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: _.get(file, 'dimension.width'),
        height: _.get(file, 'dimension.height'),
        storyboard: storyboardId,
        target: target,
      }

      if (file.type === 'video/mp4') {
        var video = document.createElement('video');

        video.onloadedmetadata = () => {
          request = {
            ...request,
            width: video.videoWidth,
            height: video.videoHeight
          }

          dispatch(uploadMedia(file.id, request, () => {
          }));
        };
        video.onerror = () => {

        };
        video.src = URL.createObjectURL(file);
      } else {
        dispatch(uploadMedia(file.id, request, () => {
        }));
      }
    }
  }

  const handleFileDrop = (acceptedFiles) => {
    const uploaders = Array.from(acceptedFiles).flatMap(file => {
      let fileType = file.type;

      if((formats?.length > 0 && (!fileType || !formats.includes(fileType)))) {
        toast(
          <FailureToast
            heading={`Failure`}
            description={`File type ${file.type} is not supported`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );
        return [];
      };

      if(acceptedFiles.length === 1) {
        let active = mediaTypes;
        if(fileType === 'video/mp4') {
          active = 'video';
        } else {
          active = 'image';
        }
        setState((prev) => ({
          ...prev,
          active
        }))
      }

      file.id = shortid();
      var updatedName = removeSpecialCharactersFromName(file.name)

      var s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_URL,
        scrubFilename: (filename) => {
          var name = removeSpecialCharactersFromName(filename);
          name = name.replace(/[^\w\d_\-.\s]+/ig, '')
          return name;
        },
        signingUrlQueryParams: {'originalName': encodeURIComponent(updatedName)},
        contentDisposition: 'auto',
        preprocess:handleFileCheck,
        uploadRequestHeaders: {},
        onFinishS3Put: handleUploadComplete,
        onProgress: handleUploadProgress,
        onError: (err) => {

        }, 
        files: [file]
      });

      return s3Uploader;
    });

    if(uploaders.length > 0) {
      uploaders.forEach(instance => {
        const upload = {
          id: instance.files[0].id,
          name: instance.files[0].name,
          type: instance.files[0].type,
          size: (instance.files[0].size / 1048576),
          progress: 0,
          instance: instance
        }
  
        dispatch(initiateMediaUpload(upload, mediaTarget));
      });
    }
  }

  const handleFileDropRejected = (fileRejections) => {
    fileRejections?.forEach(() => {
      const upload = {
        type: "",
        size: 0,
      }

      dispatch(initiateMediaUpload(upload, mediaTarget));
    });
  }

  return (
    <div className={styles.DragDropPaletteContainer}>
        <Dropzone noDrag={true} noClick={true} onDrop={() => {}} multiple={true} filesLimit={null} noDragEventsBubbling={true} onDropRejected={handleFileDropRejected}>
          {({ getRootProps, isDragActive }) => (
            <div {...getRootProps({
              className: cx(styles.dropzone, styles.emptyView, { [styles.active]: isDragActive })
            })}>
              {children(isDragActive)}
            </div>
        )}
      </Dropzone>
    </div>
  )
};
